/**
 * Per-conversation, per-tab scroll-position bookmarks.
 *
 * Stored as a single JSON blob in localStorage so a user returning to a
 * previously-viewed tab lands where they left off instead of at the top.
 *
 * Storage key is versioned so a future shape change can ship without reading
 * stale entries.
 */

const STORAGE_KEY = 'omap:scroll-bookmark:v1'
const MAX_CONVERSATIONS = 50

export type BookmarkTabId = 'analysis-report' | 'strategy-workspace'

export interface Bookmark {
  anchorId: string
  scrollOffset: number
  savedAt: number
}

type BookmarkStore = {
  [conversationId: string]: {
    [tabId in BookmarkTabId]?: Bookmark
  }
}

function readStore(): BookmarkStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed === 'object') return parsed as BookmarkStore
  } catch {
    // Corrupt or unavailable — fall through to empty store
  }
  return {}
}

function writeStore(store: BookmarkStore): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store))
  } catch {
    // Quota exceeded or unavailable — silently drop; the feature is a UX nicety
  }
}

export function getBookmark(conversationId: string, tabId: BookmarkTabId): Bookmark | null {
  const store = readStore()
  return store[conversationId]?.[tabId] ?? null
}

export function saveBookmark(
  conversationId: string,
  tabId: BookmarkTabId,
  anchorId: string,
  scrollOffset: number,
): void {
  const store = readStore()
  const convEntry = store[conversationId] || {}
  convEntry[tabId] = { anchorId, scrollOffset, savedAt: Date.now() }
  store[conversationId] = convEntry
  pruneInPlace(store)
  writeStore(store)
}

export function clearBookmark(conversationId: string, tabId: BookmarkTabId): void {
  const store = readStore()
  if (store[conversationId]) {
    delete store[conversationId][tabId]
    if (Object.keys(store[conversationId]).length === 0) {
      delete store[conversationId]
    }
    writeStore(store)
  }
}

/**
 * Prune the store to the MAX_CONVERSATIONS most-recently-touched conversations.
 * Touch time is the maximum savedAt across a conversation's tab entries.
 */
function pruneInPlace(store: BookmarkStore): void {
  const ids = Object.keys(store)
  if (ids.length <= MAX_CONVERSATIONS) return

  const touchTime = (convId: string): number => {
    const entry = store[convId]
    let latest = 0
    for (const tabKey of Object.keys(entry) as BookmarkTabId[]) {
      const bm = entry[tabKey]
      if (bm && bm.savedAt > latest) latest = bm.savedAt
    }
    return latest
  }

  ids.sort((a, b) => touchTime(b) - touchTime(a))
  for (const stale of ids.slice(MAX_CONVERSATIONS)) {
    delete store[stale]
  }
}
